// Custom field renderers (CrudField type 'custom') for the catalog configs.
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, InputAdornment, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Refresh2, Trash } from 'iconsax-reactjs';

import { adminApi } from '../../api/client';
import type { CrudCustomFieldProps } from '../../crud/types';
import { t, tLabel } from '../../i18n';
import { RemoteAutocomplete, RemoteMultiAutocomplete, makeSku, rowKey, type RemoteOption } from './shared';

/** Select whose option labels are i18n keys (translated at render time). */
export function translatedSelect(options: Array<{ value: string; key: string }>, helperKey?: string) {
  return function TranslatedSelect({ field, value, error, onChange }: CrudCustomFieldProps) {
    return (
      <TextField
        select
        fullWidth
        label={tLabel(field.label)}
        required={field.required}
        value={(value ?? '') as string}
        error={Boolean(error)}
        helperText={error || (helperKey ? t(helperKey) : undefined)}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => <MenuItem key={option.value} value={option.value}>{t(option.key)}</MenuItem>)}
      </TextField>
    );
  };
}

/** Plain text/number input with a translated helper text. */
export function helpedInput(helperKey: string, type: 'text' | 'number' | 'date' = 'text') {
  return function HelpedInput({ field, value, error, onChange }: CrudCustomFieldProps) {
    return (
      <TextField
        fullWidth
        type={type}
        label={tLabel(field.label)}
        required={field.required}
        value={(value ?? '') as string}
        error={Boolean(error)}
        helperText={error || t(helperKey)}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        onChange={(event) => onChange(type === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value)}
      />
    );
  };
}

// --- Modifiers -------------------------------------------------------------

export type ModifierOptionRow = { key: string; sku: string; item: RemoteOption | null; quantity: number | '' };

export function ModifierOptionsField({ value, error, onChange }: CrudCustomFieldProps) {
  const rows = Array.isArray(value) ? (value as ModifierOptionRow[]) : [];
  const update = (index: number, patch: Partial<ModifierOptionRow>) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={700}>{t('catalog.modifier.options')}</Typography>
        <Button size="small" startIcon={<Add size={15} />} onClick={() => onChange([...rows, { key: rowKey('opt'), sku: makeSku(), item: null, quantity: 1 }])}>{t('catalog.modifier.addOption')}</Button>
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 280 }}>{tLabel('Item')}</TableCell>
            <TableCell width={140}>{tLabel('Quantity')}</TableCell>
            <TableCell width={48} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.key}>
              <TableCell>
                <RemoteAutocomplete endpoint="items/search" requiresLocation value={row.item} onChange={(option) => update(index, { item: option })} />
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} value={row.quantity} onChange={(event) => update(index, { quantity: event.target.value === '' ? '' : Number(event.target.value) })} />
              </TableCell>
              <TableCell>
                <Tooltip title={t('action.delete')}>
                  <IconButton size="small" color="error" onClick={() => onChange(rows.filter((_, i) => i !== index))}><Trash size={16} /></IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

// --- Promotions ------------------------------------------------------------

export function PromoItemsField({ field, value, error, onChange }: CrudCustomFieldProps) {
  const selected = Array.isArray(value) ? (value as RemoteOption[]) : [];
  return (
    <RemoteMultiAutocomplete
      endpoint="items/search"
      params={{ select: 1 }}
      requiresLocation
      label={tLabel(field.label)}
      value={selected}
      onChange={onChange}
      error={error}
      helperText={t('catalog.promo.itemsTip')}
      size="medium"
    />
  );
}

export function PromoItemField({ field, value, error, onChange }: CrudCustomFieldProps) {
  return (
    <RemoteAutocomplete
      endpoint="items/search"
      params={{ select: 1 }}
      requiresLocation
      label={tLabel(field.label)}
      required={field.required}
      value={value && typeof value === 'object' ? (value as RemoteOption) : null}
      onChange={onChange}
      error={error}
      size="medium"
    />
  );
}

// --- Gift cards ------------------------------------------------------------

export function generateGiftCardNumber() {
  return String(Math.floor(1000000000 + Math.random() * 9000000000));
}

export function GiftCardNumberField({ field, value, error, editing, onChange }: CrudCustomFieldProps) {
  return (
    <TextField
      fullWidth
      label={tLabel(field.label)}
      required
      value={(value ?? '') as string}
      error={Boolean(error)}
      helperText={error || (editing ? t('catalog.giftCard.numberLocked') : t('catalog.giftCard.numberTip'))}
      InputProps={{
        readOnly: editing,
        endAdornment: editing ? undefined : (
          <InputAdornment position="end">
            <Tooltip title={t('catalog.giftCard.generate')}>
              <IconButton edge="end" onClick={() => onChange(generateGiftCardNumber())}><Refresh2 size={18} /></IconButton>
            </Tooltip>
          </InputAdornment>
        )
      }}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function GiftCardCustomerField({ field, value, error, setField, onChange }: CrudCustomFieldProps) {
  return (
    <RemoteAutocomplete
      endpoint="customers/search"
      label={tLabel(field.label)}
      placeholder={t('catalog.searchCustomer')}
      value={value && typeof value === 'object' ? (value as RemoteOption) : null}
      onChange={(option) => {
        onChange(option);
        setField('_customer_points', option?.raw?.points != null ? Number(option.raw.points) : null);
        if (!option || !Number(option.raw?.points)) setField('points', '');
      }}
      error={error}
      size="medium"
    />
  );
}

/** Points the customer can still convert (on edit the card's own points are given back). */
export function giftCardAvailablePoints(form: Record<string, unknown>) {
  const points = form._customer_points;
  if (points == null || points === '') return null;
  return Number(points) + Number(form._original_points ?? 0);
}

export function GiftCardPointsField({ field, value, error, form, setField, onChange }: CrudCustomFieldProps) {
  const customer = form.customer && typeof form.customer === 'object' ? (form.customer as RemoteOption) : null;
  const [loading, setLoading] = useState(false);
  const setFieldRef = useRef(setField);
  setFieldRef.current = setField;
  // On edit the customer option comes from the record (no points): look them up.
  useEffect(() => {
    if (!customer?.value || form._customer_points != null) return;
    let cancelled = false;
    setLoading(true);
    adminApi
      .get('customers/search', { params: { q: customer.label } })
      .then((response) => {
        const rows = Array.isArray(response.data) ? (response.data as Array<Record<string, unknown>>) : [];
        const match = rows.find((row) => String(row.value ?? row.id) === String(customer.value));
        if (!cancelled) setFieldRef.current('_customer_points', Number(match?.points ?? 0));
      })
      .catch(() => !cancelled && setFieldRef.current('_customer_points', 0))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [customer?.value, customer?.label, form._customer_points]);
  const available = giftCardAvailablePoints(form);
  if (!customer?.value) return <Box />;
  return (
    <TextField
      fullWidth
      type="number"
      label={tLabel(field.label)}
      value={(value ?? '') as string}
      disabled={loading || !available}
      error={Boolean(error)}
      helperText={error || (available ? t('catalog.giftCard.pointsAvailable', { points: available }) : t('catalog.giftCard.noPoints'))}
      onChange={(event) => onChange(event.target.value === '' ? '' : Number(event.target.value))}
    />
  );
}
